import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import './App.css';
import { useAudioContext } from './hooks/useAudioContext';
import { useQuestionFlow } from './hooks/useQuestionFlow';
import { useProgressSave } from './hooks/useProgressSave';
import { FrequencyControl } from './components/FrequencyControl';
import { VolumeControl } from './components/VolumeControl';
import { QuestionFlow } from './components/QuestionFlow';
// import { ProgressIndicator } from './components/ProgressIndicator';
import { ResultDisplayEnhanced } from './components/ResultDisplayEnhanced';
// import { AudioVisualizer } from './components/AudioVisualizer';
import { EntryPage } from './components/EntryPage';
import { CookieConsent } from './components/CookieConsent';
import { NotchedWhiteNoisePage } from './components/NotchedWhiteNoisePage';
import { Footer } from './components/Footer';
import { FrequencyInputModal } from './components/FrequencyInputModal';
import { MobileTestInterface } from './components/MobileTestInterface';
import { MobileResultView } from './components/MobileResultView';
import { useIsMobile } from './hooks/useIsMobile';
import { 
  analyticsEvents, 
  initializeAnalytics, 
  trackScrollDepth, 
  trackEngagementTime, 
  resetPageTracking,
  trackWebVitals
} from './utils/analytics';
import { useScrollToTop } from './hooks/useScrollToTop';
import DisclaimerPage from './pages/DisclaimerPage';
import HowItWorksExpanded from './pages/HowItWorksExpanded';
import LegalPage from './pages/LegalPage';
import AboutTinnitus from './pages/AboutTinnitus';
import AndroidPrivacyPage from './pages/AndroidPrivacyPage';

type AppView = 'entry' | 'onboarding' | 'assessment' | 'notched-white-noise' | 'shared';

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const { scrollToTop } = useScrollToTop(); // Auto-scrolls on route changes
  const previousPathnameRef = useRef(location.pathname);
  const [currentView, setCurrentView] = useState<AppView>('entry');
  const [volume, setVolume] = useState(0.3);
  const [hasStarted, setHasStarted] = useState(false);
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const isMobile = useIsMobile();
  const [sharedFrequency, setSharedFrequency] = useState<number | null>(null);
  const [showFrequencyModal, setShowFrequencyModal] = useState(false);
  
  
  const {
    isPlaying,
    playFrequency,
    stopFrequency,
    setVolume: setAudioVolume,
  } = useAudioContext();

  const {
    currentQuestion,
    isComplete,
    progress,
    resultFrequency,
    processAnswer,
    reset,
    getCurrentFrequency,
  } = useQuestionFlow();

  const { clearProgress } = useProgressSave();

  const currentFrequency = getCurrentFrequency();

  // Initialize analytics on mount
  useEffect(() => {
    initializeAnalytics();
    
    // Track Web Vitals
    if ('web-vital' in window) {
      (window as any).addEventListener('web-vital', (e: any) => {
        trackWebVitals({ name: e.detail.name, value: e.detail.value });
      });
    }
    
    // Global error tracking
    window.addEventListener('error', (event) => {
      analyticsEvents.trackError(
        `${event.message} at ${event.filename}:${event.lineno}:${event.colno}`,
        true
      );
    });
    
    window.addEventListener('unhandledrejection', (event) => {
      analyticsEvents.trackError(
        `Unhandled Promise Rejection: ${event.reason}`,
        false
      );
    });
  }, []);

  // Track page views and engagement
  useEffect(() => {
    // Reset tracking for new page
    resetPageTracking();
    
    // Track page view
    const pageName = location.pathname === '/' ? 'Home' : 
                    location.pathname.slice(1).split('-').map(word => 
                      word.charAt(0).toUpperCase() + word.slice(1)
                    ).join(' ');
    analyticsEvents.viewPage(pageName);
    
    // Set up scroll tracking
    const handleScroll = () => trackScrollDepth();
    window.addEventListener('scroll', handleScroll);
    
    // Set up engagement time tracking
    const interval = setInterval(() => trackEngagementTime(), 5000);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (interval) clearInterval(interval);
    };
  }, [location.pathname]);

  // Sync route with view and stop audio when changing routes
  useEffect(() => {
    const previousPathname = previousPathnameRef.current;
    
    // Only stop audio when actually changing routes
    if (previousPathname !== location.pathname) {
      // Stop audio when leaving assessment page
      if (previousPathname === '/assessment' && location.pathname !== '/assessment') {
        stopFrequency();
      }
      // Update the ref for next comparison
      previousPathnameRef.current = location.pathname;
    }
    
    if (location.pathname === '/notched-white-noise') {
      setCurrentView('notched-white-noise');
    } else if (location.pathname === '/assessment') {
      // Reset test state when navigating to assessment
      if (previousPathname !== '/assessment') {
        reset();
        setHasStarted(false);
        setIsWaitingForResponse(false);
        setIsPaused(false);
        clearProgress();
      }
      setCurrentView('assessment');
    } else if (location.pathname === '/') {
      setCurrentView('entry');
    }
  }, [location.pathname, stopFrequency, reset, clearProgress]);

  // Check for existing progress on mount
  useEffect(() => {
    // Clear any saved progress on mount to ensure fresh start
    clearProgress();
    
    // Check URL params for shared frequency
    const params = new URLSearchParams(window.location.search);
    const sharedFreq = params.get('freq');
    if (sharedFreq) {
      const freq = parseInt(sharedFreq);
      if (!isNaN(freq) && freq >= 20 && freq <= 20000) {
        setSharedFrequency(freq);
        setCurrentView('shared');
      }
    }
  }, [clearProgress]);

  // Removed progress saving to prevent persistence between sessions


  // Track assessment completion and scroll to results
  useEffect(() => {
    if (isComplete && resultFrequency && hasStarted) {
      // Stop any playing audio when assessment completes
      stopFrequency();
      // Track completion with frequency and iterations
      analyticsEvents.completeAssessment(resultFrequency, parseInt(currentQuestion.id));
      // Clear progress on completion
      clearProgress();
      // Scroll to top to show results
      scrollToTop('smooth');
    }
  }, [isComplete, resultFrequency, hasStarted, currentQuestion.id, clearProgress, scrollToTop, stopFrequency]);

  const handlePlay = () => {
    if (isPaused) {
      return; // Don't play if paused
    }
    if (!hasStarted) {
      setHasStarted(true);
      // Track assessment start
      analyticsEvents.startAssessment();
    }
    playFrequency(currentFrequency);
    setIsWaitingForResponse(true);
    
    // Track frequency playback
    analyticsEvents.trackFeatureInteraction('frequency_control', 'play', currentFrequency);
  };

  const handleStop = () => {
    stopFrequency();
  };

  const handleVolumeChange = useCallback((newVolume: number) => {
    setVolume(newVolume);
    setAudioVolume(newVolume);
    // Track volume adjustment
    analyticsEvents.adjustVolume(newVolume);
  }, [setAudioVolume]);


  const handleAnswer = (answer: string | number) => {
    // Stop current sound first
    stopFrequency();
    
    const nextFrequency = processAnswer(answer);
    setIsWaitingForResponse(false);
    
    if (nextFrequency !== null) {
      setIsTransitioning(true);
      // Automatically play the next frequency after a short delay
      setTimeout(() => {
        playFrequency(nextFrequency);
        setIsWaitingForResponse(true);
        setIsTransitioning(false);
      }, 700);
    } else {
      // Assessment completed - ensure audio stays stopped
      stopFrequency();
    }
  };

  const handleReset = () => {
    reset();
    setHasStarted(false);
    setIsWaitingForResponse(false);
    stopFrequency();
    setCurrentView('entry');
    navigate('/');
    // Scroll to top when resetting
    setTimeout(() => scrollToTop('smooth'), 100);
  };

  const handlePauseResume = () => {
    if (isPaused) {
      setIsPaused(false);
      if (isWaitingForResponse) {
        playFrequency(currentFrequency);
      }
      analyticsEvents.resumeAssessment();
    } else {
      setIsPaused(true);
      stopFrequency();
      analyticsEvents.pauseAssessment();
    }
  };

  // Navigation handlers
  const handleStartAssessment = () => {
    // Reset everything for a fresh start
    reset();
    setHasStarted(false);
    setIsWaitingForResponse(false);
    setIsPaused(false);
    clearProgress();
    stopFrequency();
    
    navigate('/assessment');
    // Scroll to top for new assessment view
    setTimeout(() => scrollToTop('smooth'), 100);
  };


  const handleBackToEntry = () => {
    stopFrequency(); // Stop any playing audio
    navigate('/');
  };

  const handleKnowFrequency = () => {
    stopFrequency(); // Stop any playing audio
    setShowFrequencyModal(true);
  };

  const handleFrequencySet = (frequency: number) => {
    setShowFrequencyModal(false);
    navigate(`/notched-white-noise?freq=${frequency}`);
  };

  const handleFrequencyModalCancel = () => {
    setShowFrequencyModal(false);
  };


  // Main app content (assessment flow)
  const renderMainContent = () => {
    // Render entry page
    if (currentView === 'entry') {
      return (
        <>
          <EntryPage 
            onStartAssessment={handleStartAssessment}
            onKnowFrequency={handleKnowFrequency}
          />
          <CookieConsent />
        </>
      );
    }


    if (currentView === 'shared' && sharedFrequency) {
      return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
          {/* Medical Header with Navigation */}
          <header className="bg-white border-b-4 border-purple-500 shadow-md">
            <div className="container mx-auto px-4 py-5">
              <div className="flex flex-col sm:flex-row items-center justify-between">
                <div className="flex items-center gap-4 mb-4 sm:mb-0">
                  <button 
                    onClick={handleBackToEntry}
                    className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center text-white text-2xl font-bold hover:from-purple-600 hover:to-pink-600 transition-all duration-200 cursor-pointer"
                  >
                    🔊
                  </button>
                  <div>
                    <h1 className="text-2xl sm:text-3xl font-semibold text-purple-700">
                      TinnitusTune
                    </h1>
                    <p className="text-gray-500 text-sm">
                      Shared Frequency: {sharedFrequency} Hz
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </header>

          <div className="container mx-auto px-4 py-8 flex-grow">
            <div className="max-w-4xl mx-auto">
              <div className="medical-card p-8">
                <h2 className="text-2xl font-semibold text-purple-700 mb-6 text-center">
                  Shared Tinnitus Frequency
                </h2>
                
                <div className="medical-frequency-display mb-6">
                  <div className="text-lg opacity-90 mb-2">Frequency</div>
                  <div className="text-5xl font-light">{sharedFrequency} Hz</div>
                </div>

                <div className="mb-6">
                  <VolumeControl
                    volume={volume}
                    onVolumeChange={handleVolumeChange}
                  />
                </div>

                <div className="text-center mb-6">
                  <FrequencyControl
                    frequency={sharedFrequency}
                    isPlaying={isPlaying}
                    onPlay={() => playFrequency(sharedFrequency)}
                    onStop={handleStop}
                    playText="Play Sound"
                  />
                </div>

                <div className="bg-purple-50 border border-purple-200 rounded-xl p-6 mb-6">
                  <h4 className="text-lg font-semibold text-purple-700 mb-3">ℹ️ About This Frequency</h4>
                  <p className="text-gray-700">Someone has shared their tinnitus frequency with you. You can play this frequency to understand what they experience. Remember that tinnitus perception varies between individuals.</p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={handleStartAssessment}
                    className="medical-button px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-all duration-200"
                  >
                    Find Your Own Frequency
                  </button>
                  <button
                    onClick={() => navigate(`/notched-white-noise?freq=${sharedFrequency}`)}
                    className="medical-button px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-all duration-200"
                  >
                    Try Notched White Noise
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          {/* Footer */}
          <Footer />
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        {/* Minimal Context Header */}
        <header className="bg-white border-b border-gray-100 py-3">
          <div className="container mx-auto px-4">
            <div className="text-center">
              {hasStarted && !isComplete && (
                <p className="text-sm text-gray-600">
                  Testing: <span className="font-medium text-purple-700">{currentFrequency} Hz</span>
                </p>
              )}
              {!hasStarted && (
                <p className="text-sm text-gray-600">TinnitusTune Frequency Test</p>
              )}
            </div>
          </div>
        </header>


        <div className={`container mx-auto px-4 py-8 flex-grow ${isMobile && !hasStarted ? 'hidden' : ''}`}>
          <div className="max-w-2xl mx-auto">
            <div className="medical-card p-8">
                  <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-purple-700">
                      What's your tinnitus frequency?
                    </h2>
                    <p className="text-gray-600 mt-2">Quick, accurate test - no registration needed</p>
                  </div>

                  {!isComplete ? (
                    <>
                      {/* Simple Progress */}
                      {hasStarted && (
                        <div className="mb-8">
                          <div className="flex justify-between text-sm text-gray-600 mb-2">
                            <span>Question {currentQuestion.id}</span>
                            <span>{progress}% complete</span>
                          </div>
                          <div className="h-2 bg-purple-100 rounded-full">
                            <div 
                              className="h-full bg-gradient-to-r from-purple-600 to-pink-600 rounded-full transition-all duration-300"
                              style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
                            />
                          </div>
                        </div>
                      )}

                      {/* Pause Overlay */}
                      {isPaused && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                          <div className="bg-white rounded-lg shadow-2xl p-8 max-w-md mx-4">
                            <h2 className="text-2xl font-bold text-purple-700 mb-4 text-center">
                              Assessment Paused
                            </h2>
                            <p className="text-gray-700 mb-6 text-center">
                              Your progress has been saved. Click resume when you're ready to continue.
                            </p>
                            <div className="flex gap-4 justify-center">
                              <button
                                onClick={handleReset}
                                className="medical-button px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white font-semibold rounded-lg transition-all duration-200"
                              >
                                Start Over
                              </button>
                              <button
                                onClick={handlePauseResume}
                                className="medical-button px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-all duration-200"
                              >
                                Resume
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Current Frequency Display */}
                      <div className="text-center py-8 mb-8 bg-purple-50 rounded-xl border border-purple-200">
                        <div className="text-4xl font-bold text-purple-700 mb-2">
                          {currentFrequency} Hz
                        </div>
                        <div className="text-gray-600">
                          Test Frequency
                        </div>
                      </div>

                      {/* Inline Volume Control */}
                      <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-gray-700 font-medium">Volume</span>
                          <span className="text-purple-700 font-mono text-sm">
                            {Math.round(volume * 100)}%
                          </span>
                        </div>
                        <div className="flex items-center gap-4">
                          <button 
                            onClick={() => handleVolumeChange(Math.max(0, volume - 0.1))}
                            className="w-10 h-10 bg-gray-200 hover:bg-gray-300 rounded-lg flex items-center justify-center text-gray-600 transition-colors text-lg font-bold"
                          >
                            −
                          </button>
                          <div 
                            className="flex-1 h-4 bg-gray-200 rounded-full cursor-pointer"
                            onClick={(e) => {
                              const rect = e.currentTarget.getBoundingClientRect();
                              const x = e.clientX - rect.left;
                              const width = rect.width;
                              const newVolume = Math.max(0, Math.min(1, x / width));
                              handleVolumeChange(newVolume);
                            }}
                          >
                            <div 
                              className="h-full bg-gradient-to-r from-purple-600 to-pink-600 rounded-full transition-all pointer-events-none" 
                              style={{ width: `${volume * 100}%` }}
                            />
                          </div>
                          <button 
                            onClick={() => handleVolumeChange(Math.min(1, volume + 0.1))}
                            className="w-10 h-10 bg-gray-200 hover:bg-gray-300 rounded-lg flex items-center justify-center text-gray-600 transition-colors text-lg font-bold"
                          >
                            +
                          </button>
                        </div>
                      </div>


                      {/* Contextual Help */}
                      {!hasStarted && (
                        <div className="bg-purple-50 border border-purple-200 rounded-xl p-6 mb-8">
                          <h3 className="font-semibold text-purple-900 mb-3">Ready to start?</h3>
                          <ul className="space-y-2 text-purple-800">
                            <li>• Put on headphones</li>
                            <li>• Find a quiet space</li>
                            <li>• Set comfortable volume</li>
                            <li>• Takes about 5 minutes</li>
                          </ul>
                        </div>
                      )}

                      {isWaitingForResponse && (
                        <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-8">
                          <h3 className="font-semibold text-green-900 mb-3">Compare to your tinnitus</h3>
                          <p className="text-green-800">Is this sound higher, lower, or a match?</p>
                        </div>
                      )}

                      {isTransitioning && (
                        <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-8">
                          <h3 className="font-semibold text-green-900 mb-3">Adjusting frequency...</h3>
                          <p className="text-green-800">The next sound will play automatically</p>
                        </div>
                      )}

                      {/* Main Action Button - shown on desktop always, on mobile only when not started */}
                      <div className={`text-center mt-8 ${hasStarted ? 'hidden md:block' : 'block'}`}>
                        <FrequencyControl
                          frequency={currentFrequency}
                          isPlaying={isPlaying}
                          onPlay={handlePlay}
                          onStop={handleStop}
                        />
                      </div>

                      {/* Question Flow - hidden on mobile when mobile interface is active */}
                      {hasStarted && (
                        <div className="mt-8 hidden md:block">
                          <QuestionFlow
                            currentQuestion={currentQuestion}
                            onAnswer={handleAnswer}
                            isWaitingForResponse={isWaitingForResponse}
                          />
                        </div>
                      )}


                    </>
                  ) : (
                    <>
                      {/* Result Display */}
                      {resultFrequency && (
                        <>
                          <ResultDisplayEnhanced
                            frequency={resultFrequency}
                            iterations={parseInt(currentQuestion.id)}
                            onReset={handleReset}
                          />
                        </>
                      )}
                    </>
                  )}
            </div>
          </div>
        </div>

        {/* Mobile Test Interface - shown on mobile for entire assessment */}
        {currentView === 'assessment' && !isComplete && (
          <MobileTestInterface
            frequency={currentFrequency}
            isPlaying={isPlaying}
            isWaitingForResponse={isWaitingForResponse}
            onPlay={handlePlay}
            onStop={handleStop}
            onAnswer={handleAnswer}
            volume={volume}
            onVolumeChange={handleVolumeChange}
            hasStarted={hasStarted}
          />
        )}

        {/* Mobile Results View - shown on mobile when assessment is complete */}
        {currentView === 'assessment' && isComplete && isMobile && resultFrequency && (
          <MobileResultView
            frequency={resultFrequency}
            onContinueToTherapy={() => {
              navigate(`/notched-white-noise?freq=${resultFrequency}`);
              setTimeout(() => scrollToTop('smooth'), 100);
            }}
            onRestart={handleReset}
          />
        )}


        {/* Cookie Consent */}
        <CookieConsent />
        
        {/* Footer */}
        <Footer />
      </div>
    );
  };

  return (
    <>
      <Routes>
        <Route path="/" element={renderMainContent()} />
        <Route path="/assessment" element={renderMainContent()} />
        <Route path="/notched-white-noise" element={<NotchedWhiteNoisePage />} />
        <Route path="/disclaimer" element={<DisclaimerPage />} />
        <Route path="/faq" element={<Navigate to="/about-tinnitus" replace />} />
        <Route path="/how-it-works" element={<HowItWorksExpanded />} />
        <Route path="/about-tinnitus" element={<AboutTinnitus />} />
        <Route path="/legal" element={<LegalPage />} />
        <Route path="/privacy/android" element={<AndroidPrivacyPage />} />
        <Route path="/privacy-policy" element={<Navigate to="/legal" replace />} />
        <Route path="/terms-of-service" element={<Navigate to="/legal" replace />} />
      </Routes>
      
      {/* Global Frequency Input Modal */}
      <FrequencyInputModal
        isOpen={showFrequencyModal}
        onFrequencySet={handleFrequencySet}
        onCancel={handleFrequencyModalCancel}
      />
    </>
  );
}

export default App;